// Web Push helpers for the mobile companion app. iOS only allows this from an app that was
// added to the Home Screen, and the permission prompt must come from a real tap.
import { api } from "@/lib/api";

const b64ToUint8 = (value) => {
  const pad = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;

export const permissionState = () => ("Notification" in window ? Notification.permission : "unsupported");

async function registration() {
  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  return reg;
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await registration();
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Ask for permission, subscribe, and store the subscription against the signed-in user. */
export async function enablePush() {
  if (!pushSupported()) throw new Error("This browser cannot show push notifications.");
  if (!isStandalone() && /iphone|ipad|ipod/i.test(navigator.userAgent))
    throw new Error("On iPhone, add this app to your Home Screen first, then open it from the icon.");
  if (Notification.permission === "denied")
    throw new Error("Notifications are blocked. Enable them for this app in iPhone Settings → Notifications.");

  const { data: cfg } = await api.get("/push/config");
  if (!cfg.enabled || !cfg.public_key) throw new Error("Push notifications are not configured on the server.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");

  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToUint8(cfg.public_key),
    });
  }
  const { data } = await api.put("/push/subscription", { ...sub.toJSON(), ua: navigator.userAgent });
  return data;
}

/** Unsubscribe this device only. */
export async function disablePush() {
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await api.delete(`/push/subscription?endpoint=${encodeURIComponent(sub.endpoint)}`);
  } finally {
    await sub.unsubscribe();
  }
}

export const sendTestPush = () => api.post("/push/test");
